import axios, { AxiosResponse, AxiosInstance, CancelTokenSource, CancelToken, AxiosError, InternalAxiosRequestConfig } from 'axios';
import * as Auth from './auth';
import { message } from 'antd';

export type { CancelTokenSource }; // TODO: Remove this once all existing places are refactored

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '',
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

axiosClient.interceptors.request.use((axiosConfig: InternalAxiosRequestConfig) => {
  const token = Auth.GetToken();
  if (token) {
    axiosConfig.headers.setAuthorization(`Bearer ${token}`);
  }
  return axiosConfig;
});

type ApiError = {
  message?: string | string[];
};

axiosClient.interceptors.response.use(
  (successResponse: AxiosResponse) => {
    return successResponse.data;
  },
  (errorResponse: AxiosError<ApiError>) => {
    if (errorResponse.response) {
      // Whenever the backend sends a 401, clear the token and redirect to sign-in
      if (errorResponse.response.status === 401) {
        Auth.Logout();
        window.location.href = '/sign-in';
        return;
      }
      let errorMessage;
      if (errorResponse.response.data.message instanceof Array) {
        errorMessage = errorResponse.response.data.message.join('\n');
      } else {
        errorMessage = errorResponse.response.data.message;
      }
      message.error(errorMessage);
      return Promise.reject(errorMessage);
    } else {
      return Promise.reject(errorResponse);
    }
  },
);

export class Api {
  static getInstance = (): AxiosInstance => axiosClient;
  static getCancelTokenSource = (): CancelTokenSource => axios.CancelToken.source();
  static isCancel = (err: Error): boolean => axios.isCancel(err);
  static parseError = (response: AxiosResponse): string => {
    if (response.data && response.data.message && response.data.message instanceof Array) {
      return response.data.message.join('\n');
    }
    return response.data.message;
  };

  static async tryCatch<T>(promise: Promise<T>, ok: (result: T) => void, fail: (err: Error) => void) {
    try {
      const rs = await promise;
      ok(rs);
    } catch (err) {
      if (Api.isCancel(err as Error)) return;
      fail(err as Error);
    } finally {
      return Promise.resolve();
    }
  }

  static get = <ParamsType extends Record<string, any>, ReturnType>(url: string, queryParams?: ParamsType, cancelToken?: CancelToken): Promise<ReturnType> => {
    return axiosClient.get(url, {
      params: queryParams,
      cancelToken,
    });
  };
  // static downloadUrl(url: string): string {
  //   return `${axiosClient.defaults.baseURL}/${url}?token=${Auth.GetToken()}`;
  // }
  static downloadWithSignedUrl(url: string): Promise<void> {
    return axiosClient.get<any, string>(url).then((signedUrl: string) => {
      // Safari does not open a new window from inside an async function, this is a hack to bypass this
      // https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
      setTimeout(() => {
        window.open(signedUrl, '_blank');
      });
    });
  }
  // static download = <ParamsType extends Record<string, any>, ReturnType extends any>(filename: string, url: string, queryParams?: ParamsType, cancelToken?: CancelToken): Promise<any> => {
  //   return axiosClient
  //     .get(url, {
  //       params: queryParams,
  //       cancelToken,
  //       responseType: 'blob',
  //     })
  //     .then((blob) => {
  //       if (window.navigator.msSaveOrOpenBlob) {
  //         // IE11
  //         window.navigator.msSaveOrOpenBlob(blob, filename);
  //       } else {
  //         const url = window.URL.createObjectURL(blob);
  //         const link = document.createElement('a');
  //         link.href = url;
  //         link.setAttribute('download', filename);
  //         document.body.appendChild(link);
  //         link.click();
  //         document.body.removeChild(link);
  //         setTimeout(function () {
  //           // For Firefox it is necessary to delay revoking the ObjectURL
  //           window.URL.revokeObjectURL(url);
  //         }, 100);
  //       }
  //     });
  // };

  static post = <ParamsType extends Record<string, any>, ReturnType>(url: string, data?: ParamsType, cancelToken?: CancelToken): Promise<ReturnType> => {
    return axiosClient.post(url, data, { cancelToken });
  };

  static postBlob = <ParamsType extends Record<string, any>>(url: string, data?: ParamsType, cancelToken?: CancelToken): Promise<Blob> => {
    return axiosClient.post(url, data, { cancelToken, responseType: 'blob' });
  };

  static put = <ParamsType extends Record<string, any>, ReturnType>(url: string, data?: ParamsType, cancelToken?: CancelToken): Promise<ReturnType> => {
    return axiosClient.put(url, data, { cancelToken });
  };

  static delete = <ParamsType extends Record<string, any>, ReturnType>(url: string, params?: ParamsType, cancelToken?: CancelToken): Promise<ReturnType> => {
    return axiosClient.delete(url, { params, cancelToken });
  };

  static patch = <ParamsType extends Record<string, any>, ReturnType>(url: string, data?: ParamsType, cancelToken?: CancelToken): Promise<ReturnType> => {
    return axiosClient.patch(url, data, { cancelToken });
  };
}
